/**
 * Sprint 22 — tool-call progress strip (ported from Sprint 6.1). Reads the
 * `data-progress` UI part from the stream and renders a done/active/pending
 * step strip, auto-scrolling to the active step.
 */
import { useEffect, useRef, useState } from "react";
import type { ProgressStep } from "../../../hooks/use-chat-progress";

interface ProgressStripProps {
  steps: ProgressStep[];
}

export function ProgressStrip({ steps }: ProgressStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [steps]);

  useEffect(() => {
    const activeIndex = steps.findIndex((s) => s.status === "active");
    if (activeIndex >= 0 && scrollRef.current) {
      const el = scrollRef.current.children[activeIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [steps]);

  useEffect(() => {
    const allDone = steps.length > 0 && steps.every((s) => s.status === "done");
    if (allDone) {
      const timer = setTimeout(() => setVisible(false), 3000);
      return () => clearTimeout(timer);
    }
    setVisible(true);
  }, [steps]);

  if (!visible || dismissed || steps.length === 0) return null;

  return (
    <div className="border-b border-white/5 bg-black/20 px-4 py-2 overflow-x-auto scrollbar-hide">
      <div ref={scrollRef} className="flex items-center gap-2 whitespace-nowrap text-sm">
        {steps.map((step, i) => (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <span className="text-white/20">&rsaquo;</span>}
            <span className={stepClass(step.status)}>
              {stepIcon(step.status)} {step.name}
            </span>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ml-auto shrink-0 text-white/20 hover:text-white/50 transition-colors text-xs px-1"
          title="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function stepIcon(status: ProgressStep["status"]): string {
  switch (status) {
    case "done": return "✓";
    case "active": return "●";
    case "pending": return "○";
  }
}

function stepClass(status: ProgressStep["status"]): string {
  switch (status) {
    case "done": return "text-white/40";
    case "active": return "text-[color:var(--color-accent)] font-medium animate-pulse";
    case "pending": return "text-white/30";
  }
}
